import React, { useContext, useState, forwardRef, useEffect } from 'react';
import { useTranslation } from '@/app/i18n/client';
import languageContext from '@/context/language';
import { Button, Favorite, Spinner } from '@/shared/components';
import { ProductDocument } from './product-document';
import { Reorder } from './reorder';
import { iconClass, gridClassName } from '../constant';
import { useRouter } from 'next/navigation';
import { updateFavorite } from '@/app/utils/check-favorite';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { usePriceFormat } from '@/hooks/use-price-format';
import { useQueryHoldCode } from '@/hooks/product-detail.hook';
import HoldCodeMessage from '@/shared/pageComponents/hold-code-message';
import { useCountry } from '@/hooks/use-country';
import { usePlacement, useProductClickEventTracking } from '@/shared/tracking/use-tracking-helpers';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { buildPdpLink } from '@/app/utils/build-pdp-link';
import { ProductImage } from '@/shared/pageComponents';
import { OrderItem } from '@/types/order';
import { useQueryCoaDocument } from '@/hooks/orderhistory.hook';
import { mapOrderStatusKey } from '@/app/utils/constant';
import { Cancel } from './cancel-button';

interface ProductProps {
  product: OrderItem;
  isFavorite?: boolean;
  index: number;
  isCancel?: boolean;
  orderNumber: string;
  poNumber?: string;
}
export const ProductItem = forwardRef<HTMLInputElement, ProductProps>(function ProductItem(props, ref) {
  const { product, isFavorite, index, isCancel, orderNumber, poNumber } = props;
  const { lang } = useContext(languageContext);
  const { t } = useTranslation(lang, 'orderhistory');
  const [isFav, setIsFav] = useState<boolean>();
  const [isFavText, setIsFavText] = useState<any>(t('save.list'));
  const getFormattedPrice = usePriceFormat();
  const [isOpen, setIsOpen] = useState(false);
  const [showLoader, setLoader] = useState(false);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { isCN } = useCountry();
  const { data: holdCodeResponse } = useQueryHoldCode(product.catalogNumber, isCN);
  const { trackEvent } = useEventTracking();
  const placement = usePlacement();
  const { data: docs } = useQueryCoaDocument(product?.lotNumber, product?.catalogNumber);

  const router = useRouter();

  useEffect(() => {
    setIsFav(isFavorite);
  }, [isFavorite]);

  useEffect(() => {
    isFav ? setIsFavText(t('saved.list')) : setIsFavText(t('save.list'));
  }, [isFav]);

  const updateFav = () => {
    setLoader(true);
    if (isFav) {
      trackEvent({
        key: 'eventsData',
        action: 'removeFromFavoritesList',
        payload: {
          event_name: 'removeFromFavoritesList',
          contentType: 'Product',
          products: [
            {
              catalogId: product.catalogNumber,
              name: product.catalogName,
              placement,
            },
          ],
        },
      });
    } else {
      trackEvent({
        key: 'eventsData',
        action: 'addToFavoritesList',
        payload: {
          event_name: 'addToFavoritesList',
          contentType: 'Product',
          products: [
            {
              catalogId: product.catalogNumber,
              name: product.catalogName,
              quantity: product.quantity,
              price: product.itemSubTotal,
              placement,
            },
          ],
        },
      });
    }

    updateFavorite([product.catalogNumber], isFav).then((res: any) => {
      if (res.code === '200' && res.message === 'success') {
        setIsFav(!isFav);
        setLoader(false);
        setIsOpen(true);
        setTimeout(() => {
          setIsOpen(false);
        }, 5e3);
      }
    });
  };

  const productClickEventTracking = useProductClickEventTracking();

  return (
    <div className={'border-0 border-b border-solid border-gray-450'}>
      <div
        className={`${gridClassName} border-0 border-solid border-gray-450 mt-6 pb-6 last:border-b-0`}
        style={{
          pageBreakBefore: 'auto',
        }}
      >
        <ProductImage width={90} height={90} src={product?.productImage} className="mr-2" />
        <div className="flex flex-col">
          <span
            dangerouslySetInnerHTML={{ __html: product.brandName ?? 'Thermo Scientific&trade;' }}
            className="text-xs font-normal text-gray-900"
          ></span>
          <Button
            type="link"
            className="p-0 mt-[6px] text-left flex items-center h-auto cursor-pointer disabled:cursor-not-allowed disabled:bg-transparent disabled:border-none disabled:text-[#A5A5AE]"
            disabled={product.productStatus === 'DISCONTINUED'}
            onClick={() => {
              productClickEventTracking(product, index);
              router.push(buildPdpLink(product?.catalogNumber));
            }}
          >
            <span
              className="leading-none text-base font-bold"
              dangerouslySetInnerHTML={{ __html: product?.catalogName }}
            ></span>
          </Button>
          <span className="mt-1">
            <span className="text-sm font-semibold">{t('catalog.number')}:</span>
            <span className="ml-2 text-sm ">{product?.webCatalogNumber}</span>
          </span>
          {isCN && (
            <span className="mt-1">
              <span className="text-sm font-semibold">{t('other.catalog')}:</span>
              <span className="ml-2 text-sm ">{product?.mpn}</span>
            </span>
          )}
          <span className="mt-1">
            <span className="text-sm font-semibold">{t('unit.size')}:</span>
            <span className="ml-2 text-sm" dangerouslySetInnerHTML={{ __html: product?.unitSize }}></span>
          </span>
        </div>
        <div className="flex flex-col">
          <span className="mt-1">
            <span
              className={`p-1 text-[#1e8ae7] ${
                iconClass[product?.itemStatus] ? iconClass[product?.itemStatus] : 'icon-16-stopwatch-mono'
              }`}
            ></span>
            <span className="text-sm font">{commonT(mapOrderStatusKey(product?.itemStatus))}</span>
          </span>
        </div>
        <div className="flex flex-col">
          <span className="mt-1">
            <span className="text-sm font">{product?.quantity}</span>
          </span>
        </div>
        <div className="flex flex-col text-right">
          <span className="mt-1">
            <span className="text-sm font-semibold">{commonT('yourPrice')}:</span>
            <span className="ml-2 text-sm ">{getFormattedPrice(product?.bestPrice)}</span>
          </span>
          <span className="mt-1">
            <span className="text-sm font-semibold">{t('total.price')}:</span>
            <span className="ml-2 text-sm ">{getFormattedPrice(product?.itemSubTotal)}</span>
          </span>
        </div>
        <HoldCodeMessage className="mt-2 col-span-full col-start-2" holdCodes={holdCodeResponse?.data} />
      </div>

      <div className={`grid grid-cols-[120px_1fr] gap-x-2 mt-6 pb-6`}>
        <div className="flex flex-col"></div>
        <div className="flex">
          <ProductDocument coa={docs?.data || []} />
          <div className="relative">
            <div
              className="flex border border-solid border-gray-300 w-max px-4 py-1 rounded-2xl mr-4 font-normal text-xs cursor-pointer"
              onClick={updateFav}
            >
              {isFavText}
              {showLoader && (
                <p className="pl-2 pt-0.5">
                  <Spinner isLoading={showLoader} className={'w-3 h-3'} />
                </p>
              )}
            </div>
          </div>
          <Favorite
            isFav={isFav}
            isOpen={isOpen}
            title={
              isFav
                ? commonT('favorite.added').replace('%s', product?.catalogNumber)
                : commonT('favorite.removed').replace('%s', product?.catalogNumber)
            }
            closeFunc={() => {
              setIsOpen(false);
            }}
          />
          <Reorder product={product} pageType="order history" />
          {isCancel && (
            <Cancel
              orderNumber={orderNumber}
              poNumber={poNumber}
              catalogNumber={product?.catalogNumber}
              webOrderNumber={product?.webOrderNumber}
              id={product?.webOrderItemId}
            />
          )}
        </div>
      </div>
    </div>
  );
});
